export type DeviceClass = 'mobile' | 'tablet' | 'desktop' | 'unknown';
export type OSFamily = 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Linux' | 'ChromeOS' | 'unknown';
export type BrowserFamily = 'Chrome' | 'Safari' | 'Firefox' | 'Edge' | 'Samsung Internet' | 'unknown';

export type DeviceContext = {
  device_class: DeviceClass;
  os_family: OSFamily;
  browser_family: BrowserFamily;
  touch_capable: boolean;
  viewport_width: number;
  viewport_height: number;
};

type NavigatorUADataBrand = {
  brand: string;
  version?: string;
};

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    brands?: NavigatorUADataBrand[];
    mobile?: boolean;
    platform?: string;
  };
};

const TABLET_UA_PATTERN = /iPad|Tablet|PlayBook|Silk|Kindle|Nexus 7|Nexus 9|SM-T|Tab|Android(?!.*Mobile)/i;
const MOBILE_UA_PATTERN = /Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i;

const UNKNOWN_DEVICE_CONTEXT: DeviceContext = {
  device_class: 'unknown',
  os_family: 'unknown',
  browser_family: 'unknown',
  touch_capable: false,
  viewport_width: 0,
  viewport_height: 0,
};

const isIPadOSLike = (navigatorObject: Navigator): boolean =>
  navigatorObject.platform === 'MacIntel' && navigatorObject.maxTouchPoints > 1;

const getViewportDimension = (dimension: 'width' | 'height'): number => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const candidate = dimension === 'width' ? window.innerWidth : window.innerHeight;
  if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
    return candidate;
  }

  if (typeof document !== 'undefined') {
    const fallback = dimension === 'width'
      ? document.documentElement?.clientWidth
      : document.documentElement?.clientHeight;
    if (typeof fallback === 'number' && Number.isFinite(fallback) && fallback > 0) {
      return fallback;
    }
  }

  return 0;
};

const getTouchCapability = (navigatorObject: Navigator): boolean => {
  if (navigatorObject.maxTouchPoints > 0) {
    return true;
  }

  return typeof window !== 'undefined' && 'ontouchstart' in window;
};

const getBrowserFamilyFromBrands = (brands: NavigatorUADataBrand[] | undefined): BrowserFamily => {
  if (!Array.isArray(brands)) {
    return 'unknown';
  }

  const normalizedBrands = brands.map((brand) => brand.brand.toLowerCase());
  if (normalizedBrands.some((brand) => brand.includes('samsung internet'))) {
    return 'Samsung Internet';
  }
  if (normalizedBrands.some((brand) => brand.includes('edge'))) {
    return 'Edge';
  }
  if (normalizedBrands.some((brand) => brand.includes('firefox'))) {
    return 'Firefox';
  }
  if (normalizedBrands.some((brand) => brand.includes('chrome') || brand.includes('chromium'))) {
    return 'Chrome';
  }
  if (normalizedBrands.some((brand) => brand.includes('safari'))) {
    return 'Safari';
  }

  return 'unknown';
};

const getBrowserFamilyFromUserAgent = (userAgent: string): BrowserFamily => {
  if (/SamsungBrowser/i.test(userAgent)) {
    return 'Samsung Internet';
  }
  if (/EdgA?|EdgiOS/i.test(userAgent)) {
    return 'Edge';
  }
  if (/Firefox|FxiOS/i.test(userAgent)) {
    return 'Firefox';
  }
  if (/Chrome|CriOS|Chromium/i.test(userAgent)) {
    return 'Chrome';
  }
  if (/Safari/i.test(userAgent) && !/Chrome|CriOS|Chromium|Android|EdgA?|EdgiOS/i.test(userAgent)) {
    return 'Safari';
  }

  return 'unknown';
};

const getOSFamily = (navigatorObject: NavigatorWithUserAgentData, userAgent: string): OSFamily => {
  const platform = (navigatorObject.userAgentData?.platform || navigatorObject.platform || '').toLowerCase();

  if (isIPadOSLike(navigatorObject)) {
    return 'iOS';
  }
  if (platform.includes('ios')) {
    return 'iOS';
  }
  if (platform.includes('android')) {
    return 'Android';
  }
  if (platform.includes('chrome os') || platform.includes('cros')) {
    return 'ChromeOS';
  }
  if (platform.includes('windows')) {
    return 'Windows';
  }
  if (platform.includes('mac')) {
    return 'macOS';
  }
  if (platform.includes('linux')) {
    return 'Linux';
  }

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'iOS';
  }
  if (/Android/i.test(userAgent)) {
    return 'Android';
  }
  if (/CrOS/i.test(userAgent)) {
    return 'ChromeOS';
  }
  if (/Windows/i.test(userAgent)) {
    return 'Windows';
  }
  if (/Macintosh|Mac OS X/i.test(userAgent)) {
    return 'macOS';
  }
  if (/Linux|X11/i.test(userAgent)) {
    return 'Linux';
  }

  return 'unknown';
};

const getDeviceClass = (navigatorObject: NavigatorWithUserAgentData, userAgent: string): DeviceClass => {
  if (isIPadOSLike(navigatorObject)) {
    return 'tablet';
  }
  if (TABLET_UA_PATTERN.test(userAgent)) {
    return 'tablet';
  }
  if (navigatorObject.userAgentData?.mobile === true || MOBILE_UA_PATTERN.test(userAgent)) {
    return 'mobile';
  }

  if (userAgent || navigatorObject.platform || navigatorObject.userAgentData?.platform) {
    return 'desktop';
  }

  return 'unknown';
};

export const getDeviceContext = (): DeviceContext => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return UNKNOWN_DEVICE_CONTEXT;
  }

  const navigatorObject = navigator as NavigatorWithUserAgentData;
  const userAgent = navigatorObject.userAgent || '';
  const browserFamilyFromBrands = getBrowserFamilyFromBrands(navigatorObject.userAgentData?.brands);

  return {
    device_class: getDeviceClass(navigatorObject, userAgent),
    os_family: getOSFamily(navigatorObject, userAgent),
    browser_family:
      browserFamilyFromBrands === 'unknown'
        ? getBrowserFamilyFromUserAgent(userAgent)
        : browserFamilyFromBrands,
    touch_capable: getTouchCapability(navigatorObject),
    viewport_width: getViewportDimension('width'),
    viewport_height: getViewportDimension('height'),
  };
};

export const withDeviceContext = <T extends Record<string, unknown>>(
  payload: T,
): T & { device_context: DeviceContext } => ({
  ...payload,
  device_context: getDeviceContext(),
});
